import SoftButton from "components/SoftButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import { usePortalFlows } from "hooks/usePortalFlows";
import { useResourceDependencies } from "hooks/useResourceDependencies";
import { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";

export const DeleteIntegrationDialog = memo(({ open, onClose, onAccept, integration }) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const { flows, handleFetchFlows, areFlowsLoaded, isLoadingPortalFlows } =
    usePortalFlows(false);
  const { integrationMutatingFlag } = useSelector((store) => store?.portal?.integrations);
  const {
    dependencies: integrationFlowIds,
    isLoading: isLoadingDependencies,
    hasFetched: hasFetchedDependencies,
    error: dependenciesError
  } = useResourceDependencies({
    sourceId: integration?.id,
    direction: "reverse",
    targetResourceType: "flow",
    shouldFetch: open
  });

  useEffect(() => {
    if (!open) return;

    setIsConfirmingDelete(false);
    handleFetchFlows();
  }, [open]);

  const flowsUsingTheIntegration = integrationFlowIds
    .map((flowId) => flows.find((flow) => flow.id === flowId))
    .filter(Boolean);
  const isCheckingUsage =
    (open && (!hasFetchedDependencies || isLoadingDependencies)) ||
    (integrationFlowIds.length > 0 && (!areFlowsLoaded || isLoadingPortalFlows));
  const hasCheckFailed = Boolean(dependenciesError);

  const handleAccept = () => {
    if (isConfirmingDelete || isCheckingUsage || hasCheckFailed) return;

    setIsConfirmingDelete(true);
    onAccept();
    setIsConfirmingDelete(false);
  };

  const renderDeleteMessage = () => {
    if (!open) return <></>;

    if (isCheckingUsage) {
      return (
        <div style={{ alignItems: "center", display: "flex", gap: "12px" }}>
          <CircularProgress size={20} />
          <span>Checking flows using this integration...</span>
        </div>
      );
    }

    if (hasCheckFailed) {
      return (
        <span>
          Usage could not be verified for <strong>{integration.integration_name}</strong>. Please
          try again before deleting it.
        </span>
      );
    }

    if (flowsUsingTheIntegration.length) {
      return (
        <div>
          {flowsUsingTheIntegration.length === 1 && (
            <div style={{ marginBottom: "1rem" }}>
              This <strong>{integration.integration_name}</strong> is being used in a flow. Remove
              the
              <strong> {integration.integration_name} </strong> from the{" "}
              <strong>{flowsUsingTheIntegration[0].name}</strong> flow before deleting it.
            </div>
          )}
          {flowsUsingTheIntegration.length > 1 && (
            <>
              <div style={{ marginBottom: "1rem" }}>
                This <strong>{integration.integration_name}</strong> is being used by multiple
                flows. Make sure to remove it first from the flows before deleting it. Flows using
                is listed below.
              </div>
              <ul style={{ paddingLeft: "1rem" }}>
                {flowsUsingTheIntegration.map((flow, index) => (
                  <li key={`${index}-${flow.id}`}>
                    <strong>{flow.name}</strong>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      );
    }

    return (
      <span>
        Are you sure you want to delete this <strong>{integration.integration_name}</strong>?
      </span>
    );
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={open}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Integration</DialogTitle>
        <DialogContent>{renderDeleteMessage()}</DialogContent>
        <DialogActions>
          <SoftButton
            onClick={onClose}
            variant="outlined"
            color="info"
            disabled={integrationMutatingFlag}
          >
            {flowsUsingTheIntegration.length || hasCheckFailed ? "Close" : "Cancel"}
          </SoftButton>
          {!flowsUsingTheIntegration.length && !isCheckingUsage && !hasCheckFailed && (
            <SoftButton
              variant="contained"
              color="info"
              onClick={handleAccept}
              disabled={integrationMutatingFlag || isConfirmingDelete}
            >
              Delete
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
});
